import React, { useEffect, useMemo, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { format } from 'date-fns'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { Row } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import MyPagination from 'components/ui/MyPagination'
import Selector from 'components/ui/Selector'
import { delUrlFetcher, postFetcher } from 'api/swr'
import { DEFAULT_RANKING_COUNTRY, RANKING_COUNTRY_OPTIONS } from './constants'

const PAGE_SIZE = 100

const HOT_LANG_LABEL: Record<string, string> = {
  en: '英语',
  ja: '日语',
  es: '西班牙语',
  pt: '葡萄牙语',
  de: '德语',
}

type SavedWord = {
  id: string
  word: string
  category: string
  addedAt: number
}

type SavedListPage = {
  country: string
  kind: 'positive' | 'blacklist'
  items: SavedWord[]
  hasMore: boolean
  pageEndId: string | null
}

function formatAddedAt(ts: number) {
  if (!ts) return '—'
  return format(ts, 'yyyy-MM-dd HH:mm')
}

export default function SavedListTab({ kind }: { kind: 'positive' | 'blacklist' }) {
  const { toast } = useToast()
  const { mutate: refreshHotWords } = useSWRConfig()
  const [countryCode, setCountryCode] = useState(DEFAULT_RANKING_COUNTRY)
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])
  const [busy, setBusy] = useState('')
  const country = useMemo(
    () => RANKING_COUNTRY_OPTIONS.find((item) => item.code === countryCode),
    [countryCode],
  )
  const goAfter = (next: string | null) => {
    setAfter(next)
    if (next === null) {
      setAfters([])
      return
    }
    const index = afters.indexOf(next)
    setAfters(index === -1 ? [...afters, next] : afters.slice(0, index + 1))
  }

  useEffect(() => {
    setAfter(null)
    setAfters([])
  }, [countryCode, kind])

  const { data, isLoading, error, mutate } = useSWR<SavedListPage>(
    country?.short
      ? [
          '/search-hot-words/list-words',
          {
            kind,
            country: country.short,
            limit: PAGE_SIZE,
            ...(after ? { after } : {}),
          },
        ]
      : null,
  )
  const ready = data?.country === country?.short && data.kind === kind
  const items = ready ? data.items : []
  const nextAfter = ready && data.hasMore && data.pageEndId ? data.pageEndId : null
  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : ''

  const remove = async (word: SavedWord) => {
    if (!country?.short || busy) return
    setBusy(word.word)
    try {
      await delUrlFetcher(
        `/search-hot-words/list-words?kind=${kind}&country=${encodeURIComponent(country.short)}&word=${encodeURIComponent(word.word)}`,
        { arg: null },
      )
      await mutate()
    } catch (removeError) {
      toast({
        variant: 'destructive',
        title: '移出失败',
        description:
          removeError instanceof Error ? removeError.message : '没有从列表移出',
      })
    } finally {
      setBusy('')
    }
  }

  const promote = async (word: SavedWord) => {
    if (!country?.short || busy) return
    setBusy(word.word)
    try {
      const result = await postFetcher('/search-hot-words/promote', {
        arg: { country: country.short, word: word.word },
      })
      const lang = HOT_LANG_LABEL[result?.lang] ?? result?.lang ?? ''
      if (result?.added) void refreshHotWords(['/search-hot-words'])
      if (kind === 'positive') await mutate()
      toast({
        title: result?.added ? '已加入热词' : '已在热词中',
        description: lang ? `${lang} · ${word.word}` : word.word,
      })
    } catch (promoteError) {
      toast({
        variant: 'destructive',
        title: '加入热词失败',
        description:
          promoteError instanceof Error ? promoteError.message : '没有写进热词',
      })
    } finally {
      setBusy('')
    }
  }

  const columns = useMemo(
    () => [
      { header: '搜索词', accessorKey: 'word' },
      {
        header: kind === 'positive' ? '分类' : '风险类型',
        accessorKey: 'category',
      },
      {
        header: '加入时间',
        id: 'addedAt',
        cell: ({ row }: { row: Row<SavedWord> }) => (
          <span className="tabular-nums">{formatAddedAt(row.original.addedAt)}</span>
        ),
      },
      {
        header: '操作',
        id: 'action',
        cell: ({ row }: { row: Row<SavedWord> }) => (
          <div className="flex items-center gap-2">
            {kind === 'positive' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy === row.original.word}
                onClick={() => promote(row.original)}
              >
                加入热词
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy === row.original.word}
              onClick={() => remove(row.original)}
            >
              移出
            </Button>
          </div>
        ),
      },
    ],
    [kind, busy, country?.short],
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  const filters = (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">国家:</label>
        <Selector
          datas={RANKING_COUNTRY_OPTIONS}
          labelName="国家"
          value={countryCode}
          update={setCountryCode}
          showCode
          width="w-[220px]"
        />
      </div>
    </div>
  )

  if (isLoading && !ready) {
    return (
      <div className="space-y-4">
        {filters}
        <Loader />
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="space-y-4">
        {filters}
        <EmptyDatas />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-4">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
          className="m-0 w-auto"
        />
        <Button variant="outline" onClick={() => mutate()}>
          Refetch
        </Button>
        {filters}
      </div>
      <div className="h-[calc(100vh-254px)] flex-1">
        <Card className="flex h-full flex-col">
          <CardContent className="flex-1 overflow-hidden pb-0 pl-4 pr-0">
            <div className="h-full overflow-auto">
              {items.length === 0 ? <EmptyDatas /> : <Table table={table} />}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
